import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, CheckCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { enablePush, isPushEnabled, pushSupported } from "@/lib/push";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => { isPushEnabled().then(setPushOn); }, []);

  const toggleReminders = async () => {
    setPushBusy(true);
    try {
      await enablePush();
      setPushOn(true);
      toast.success("Reminders enabled — check for the test notification!");
    } catch (e) {
      toast.error(e.message || "Couldn't enable reminders");
    } finally {
      setPushBusy(false);
    }
  };

  const { data: count } = useQuery({
    queryKey: ["notif-count"],
    queryFn: async () => (await api.get("/notifications/unread-count")).data.count,
    refetchInterval: 20000,
  });

  const { data: items = [] } = useQuery({
    queryKey: ["notifs"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
  });

  const markAll = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notif-count"] });
    qc.invalidateQueries({ queryKey: ["notifs"] });
  };

  const open = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    qc.invalidateQueries({ queryKey: ["notif-count"] });
    qc.invalidateQueries({ queryKey: ["notifs"] });
    if (n.link) navigate(n.link);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-slate-100 transition-colors" data-testid="notification-bell">
          <Bell className="h-[18px] w-[18px] text-slate-600" />
          {count > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-heading font-semibold text-sm">Notifications</span>
          <div className="flex items-center gap-1">
            {pushSupported() && !pushOn && (
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-brand" onClick={toggleReminders} disabled={pushBusy} data-testid="enable-reminders-btn">
                <BellRing className="h-3.5 w-3.5" /> Enable reminders
              </Button>
            )}
            {pushOn && <span className="text-[11px] text-emerald-600 flex items-center gap-1" data-testid="reminders-on"><BellRing className="h-3.5 w-3.5" /> On</span>}
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={markAll} data-testid="mark-all-read-btn">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </Button>
          </div>
        </div>
        <ScrollArea className="max-h-[400px]">
          {items.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-400">No notifications yet</div>}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => open(n)}
              className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-slate-50 transition-colors flex gap-3 ${n.read ? "" : "bg-blue-50/40"}`}
              data-testid={`notification-item-${n.id}`}
            >
              <div className="mt-0.5">
                <span className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center">
                  <MessageSquare className="h-3.5 w-3.5 text-slate-500" />
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm text-slate-800 truncate">{n.title}</span>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-4 whitespace-pre-line">{n.message}</p>
                <span className="text-[11px] text-slate-400 mt-1 block">{timeAgo(n.created_at)}</span>
              </div>
            </button>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
